import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomIdInputFieldSettingsModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulatesPropsSettingsConfigFormly,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseRequiredValidatorParseFieldIntoModel,
  parsePatternValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setPatternValidatorUpdateFieldFromModel,
  setPopulatesFieldValidatorFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from 'src/app/core/models/form-fields-formly-validation.model';
import {
  CRVS_DOCUMENT_TYPE_DEFINITIONS,
  CRVSDocumentTypes,
  ICRVSDocumentTypeDefinition,
} from '../../../models/crvs-document-type.model';

export const crvsResponseProps: Set<string> = new Set([
  'data.documentType',
  'data.documentNumber',
  'data.issueNumber',
  'data.dateOfIssue',
  'data.dateOfExpiry',
  'data.placeOfIssue',
  'data.applicationNumber',
  'data.nin',
  'data.nid',
  'data.surName',
  'data.postNames',
  'data.fatherName',
  'data.motherName',
  'data.sex',
  'data.dateOfBirth',
  'data.placeOfBirth',
  'data.countryOfBirth',
  'data.birthCountry',
  'data.villageId',
  'data.domicileCountry',
  'data.domicileDistrict',
  'data.domicileProvince',
  'data.domicileSector',
  'data.domicileCell',
  'data.domicileVillage',
  'data.civilStatus',
  'data.maritalStatus',
  'data.spouse',
  'data.photo',
  'data.citizenStatus',
  'data.nationality',
  'data.applicantType',
  'data.registrationStatus',
]);

interface ICRVSFieldContentConfig {
  documentTypeKey: string;
  allowedDocumentTypes: Record<CRVSDocumentTypes, boolean>;
  useBaseUrl: boolean;
  endpointCode: string;
  mixpanelEventAnalytics?: boolean;
}

interface ICRVSConfigurationFormModel extends ICustomIdInputFieldSettingsModel {
  crvsFieldContentConfig?: ICRVSFieldContentConfig;
}

export const crvsInputGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        crvsFieldContentConfig: {
          documentTypeKey: 'DocumentTypeComponentKey',
          useBaseUrl: true,
          endpointCode: 'IGV3_FETCH_RECORD_TEST',
          allowedDocumentTypes: [],
        },
      },
      formlyForm: [
        ...getCrvsFieldSettingsConfig(formBuilderServiceInstance),
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...getPopulatesPropsSettingsConfigFormly(formBuilderServiceInstance, [
          ...Array.from(crvsResponseProps),
        ]),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const crvsFieldParseIntoSettingsFormModel = (
  crvsField: FormlyFieldConfig
): ICRVSConfigurationFormModel => {
  const model: ICRVSConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(crvsField);

  const existingTypes = crvsField.props?.['allowedDocumentTypes'] || [];
  const allowedDocumentTypes = Object.values(CRVSDocumentTypes).reduce(
    (acc, type) => ({
      ...acc,
      [type]: existingTypes.some(
        (doc: ICRVSDocumentTypeDefinition) => doc.value === type
      ),
    }),
    {} as Record<CRVSDocumentTypes, boolean>
  );

  model.crvsFieldContentConfig = {
    documentTypeKey: crvsField.props?.['documentTypeKey'],
    allowedDocumentTypes: allowedDocumentTypes,
    useBaseUrl: crvsField.props?.['useBaseUrl'],
    endpointCode: crvsField.props?.['endpointCode'],
    mixpanelEventAnalytics: crvsField.props?.['mixpanelEventAnalytics'],
  };

  return model;
};

export const crvsFieldUpdateFromSettingsFormModel = (
  model: ICRVSConfigurationFormModel,
  crvsField: FormlyFieldConfig
): FormlyFieldConfig => {
  crvsField = baseFieldUpdateFromSettingsFormModel(model, crvsField);
  crvsField = populatesPropsUpdateFromSettingsFormModel(model, crvsField);

  const documentTypeConfigs = Object.entries(
    model.crvsFieldContentConfig?.allowedDocumentTypes || {}
  )
    .filter(entry => typeof entry[1] === 'boolean' && entry[1] === true)
    .map(([type]) => {
      const definition =
        CRVS_DOCUMENT_TYPE_DEFINITIONS[type as CRVSDocumentTypes];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { label, ...configWithoutLabel } = definition;
      return configWithoutLabel;
    });

  if (crvsField.props) {
    crvsField.props['documentTypeKey'] =
      model.crvsFieldContentConfig?.documentTypeKey;
    crvsField.props['allowedDocumentTypes'] = documentTypeConfigs;
    crvsField.props['useBaseUrl'] = model.crvsFieldContentConfig?.useBaseUrl;
    crvsField.props['endpointCode'] =
      model.crvsFieldContentConfig?.endpointCode;
    crvsField.props['mixpanelEventAnalytics'] =
      model.crvsFieldContentConfig?.mixpanelEventAnalytics;
  } else {
    crvsField.props = {
      documentTypeKey: model.crvsFieldContentConfig?.documentTypeKey,
      allowedDocumentTypes: documentTypeConfigs,
      useBaseUrl: model.crvsFieldContentConfig?.useBaseUrl,
      endpointCode: model.crvsFieldContentConfig?.endpointCode,
      mixpanelEventAnalytics:
        model.crvsFieldContentConfig?.mixpanelEventAnalytics,
    };
  }

  return crvsField;
};

export const crvsFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parsePatternValidatorParseFieldIntoModel(model, field);

  return model;
};

export const crvsFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  crvsInput: FormlyFieldConfig
): FormlyFieldConfig => {
  crvsInput = setRequiredValidatorUpdateFieldFromModel(model, crvsInput);
  crvsInput = setPopulatesFieldValidatorFormDataIntoField(model, crvsInput);
  crvsInput = setPatternValidatorUpdateFieldFromModel(model, crvsInput);

  return crvsInput;
};

export const crvsFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const crvsFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getCrvsFieldSettingsConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'crvsFieldContentConfig',
      id: 'crvsFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'documentTypeKey',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            placeholder: 'Select ID Document Type Field',
            options: formBuilderServiceInstance.getAllFormFieldLabelAndDetails(
              true,
              true
            ),
            multiple: false,
            bindLabel: 'label',
            bindValue: 'value',
            required: false,
            hideHeader: true,
          },
        },
        {
          key: 'endpointCode',
          type: 'input',
          props: {
            label: 'Endpoint Code',
            required: true,
            placeholder: 'Enter Endpoint Code',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'useBaseUrl',
          type: 'checkbox',
          props: {
            label: 'Use Base URL',
            readonly: true,
          },
        },
        {
          key: 'mixpanelEventAnalytics',
          type: 'checkbox',
          defaultValue: false,
          props: { label: 'Enable MixPanel Analytics', formCheck: 'switch' },
        },
        {
          key: 'allowedDocumentTypes',
          type: 'multicheckbox',
          props: {
            label: 'Allowed Document Types',
            required: true,
            options: Object.values(CRVSDocumentTypes).map(value => ({
              label: CRVS_DOCUMENT_TYPE_DEFINITIONS[value].label,
              value,
            })),
          },
        },
      ],
    },
  ];
};
